import React, { useEffect, useState } from 'react'
import {
  CircularProgress,
  IconButton,
  Typography,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  Button
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { makeStyles } from '@material-ui/core/styles'
import { arInvoiceCols } from '../hooks/ArCols'
import { smallScreenWidth } from '../constants/AppConstants'
import LoadingButton from '../components/LoadingButton'
import SimpleAutoComplete from '../functions/SimpleAutoComplete'
import ReactTable from '../components/ReactTable'
import fetchFromAthena from '../functions/FetchFromAthena'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import DateInput from '../components/DateInput'
import HardCodedDropdown from '../components/HardCodedDropdown'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  },
  paper: {
    padding: theme.spacing(2),
    color: 'rgba(0,0,0,0.9)',
    height: '100%'
  },
  descript: { fontSize: '0.8rem', color: '#545454' },
  descriptVal: { fontSize: '0.85rem' }
}))

const defaultHeaders = {
  invoicePrefix: '',
  invoiceNumber: '',
  item: '',
  fromDate: '',
  toDate: ''
}

export default function ArInvoice (props) {
  const classes = useStyles()
  const { credentials } = props.fetchInitialData
  const [state, setstate] = useState({
    invoiceData: null,
    isLoading: false
  })
  const [headers, setHeaders] = useState(defaultHeaders)
  const [clearTrigger, setClearTrigger] = useState(false)

  const handleSubmit = async (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    setstate({ ...state, isLoading: true })

    try {
      if (!headers.item && !(headers.invoicePrefix && headers.invoiceNumber)) {
        props.fetchInitialData.createSnack(
          'Please select both Prefix and Invoice Number, or select an Item',
          'warning',
          3000
        )
        throw new Error()
      }

      const invoices = await fetchFromAthena({
        queryName: 'ar_invoice_read_headers',
        headers: {
          prefix: headers.invoicePrefix,
          invoiceNumber: headers.invoiceNumber,
          item: headers.item,
          fromDate: headers.fromDate,
          toDate: headers.toDate
        },
        accessToken: credentials.user.accessToken,
        credentials
      })

      setstate({
        ...state,
        isLoading: false,
        invoiceData: (invoices || []).map(i => {
          return {
            ...i,
            INVOICE_NUMBER: `${i.INVC_PREFIX}${i.INVC_NUMBER}`,
            VIEW: <div className='editLink'
              onClick={() => {
                const win = window.open(
                  `/ar-invoice-details?company=${i.COMPANY === '-1' ? '1' : i.COMPANY}&prefix=${i.INVC_PREFIX}&invoice=${i.INVC_NUMBER}`,
                  '_blank'
                )
                win.focus()
              }}>view</div>
          }
        })
      })
    } catch (e) {
      setstate({ ...state, isLoading: false })
    }
  }

  const handleClear = () => {
    setHeaders(defaultHeaders)
    setstate({ invoiceData: null, isLoading: false })
    setClearTrigger(prev => !prev)
  }

  const hasFilters = Object.values(headers).some(v => !!v)

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='delete'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth &&
          'Dashboard'}
      </IconButton>
      <Typography variant='h5' style={{ textAlign: 'center' }}>
        AR Invoice
      </Typography>

      <div className={classes.root}>
        <Paper elevation={0} style={{ padding: '1rem' }}>
          <form onSubmit={e => e.preventDefault()}>
            <Grid
              container
              alignContent='center'
              alignItems='flex-end'
              spacing={2}
            >
              <Grid item>
                <HardCodedDropdown
                  id='invoicePrefix'
                  width='100px'
                  label='Prefix'
                  dataset={prefixes}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, invoicePrefix: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                    id='invoiceNumber'
                    width='200px'
                    label='Invoice Number'
                    page='CUSTOMERACTIVITY'
                    entity='INVOICE'
                    searchFields={['DISPLAY']}
                    accessToken={credentials.user.accessToken}
                    credentials={credentials}
                    clearTrigger={clearTrigger}
                    onChange={val => {
                        const invoiceNumber = val?.VALUE || null
                        setHeaders({ ...headers, invoiceNumber: invoiceNumber })
                    }}
                    />
              </Grid>
              <Grid item>
                <Typography style={{ verticalAlign: 'bottom' }} variant='body1'>
                  OR
                </Typography>
              </Grid>
              <Grid item>
              <OpenSearchDropdown
                    id='item'
                    width='200px'
                    label='Item'
                    page='CUSTOMERACTIVITY'
                    entity='ITEM'
                    searchFields={['DISPLAY']}
                    accessToken={credentials.user.accessToken}
                    credentials={credentials}
                    clearTrigger={clearTrigger}
                    onChange={val => {
                        const item = val?.VALUE || null
                        setHeaders({ ...headers, item: item })
                    }}
                    />
              </Grid>
              {[
                {
                  id: 'fromDate',
                  label: 'From Date'
                },
                {
                  id: 'toDate',
                  label: 'To Date'
                }
              ].map(field => {
                return (
                    <Grid key={field.id} item>
                    <DateInput
                      id={field.id}
                      key={`${field.id}-${clearTrigger}`}
                      type={'date'}
                      required={false}
                      label={field.label}
                      value={headers[field.id]}
                      onChange={e => {
                        setHeaders({
                          ...headers,
                          [field.id]: !!e.target.value ? e.target.value : null
                        })
                      }}
                      variant='standard'
                      width='160px'
                    />
                  </Grid>
                )
              })}
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label='Submit'
                isLoading={state.isLoading}
                color='primaryVLButton'
                buttonType='button'
                fn={handleSubmit}
              />
              {hasFilters && (
                <Button
                  onClick={handleClear}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>

        {state.invoiceData && (
          <Grid
            item
            xs={12}
            style={{ position: 'relative', marginTop: '1rem' }}
          >
            <ReactTable cols={arInvoiceCols} data={state.invoiceData} />
          </Grid>
        )}
      </div>
    </div>
  )
}

const prefixes = [
  '33',
  '55',
  '66',
  '77',
  '79',
  '80',
  '99',
  'AG',
  'AK',
  'AS',
  'BC',
  'BI',
  'CA',
  'CH',
  'CL',
  'CP',
  'DF',
  'DH',
  'DJ',
  'DP',
  'DS',
  'EC',
  'EX',
  'FH',
  'FN',
  'HA',
  'HC',
  'HD',
  'HE',
  'HF',
  'HG',
  'HH',
  'HJ',
  'HK',
  'HT',
  'HV',
  'HX',
  'HZ',
  'KA',
  'LD',
  'MC',
  'MD',
  'MP',
  'MS',
  'PC',
  'PD',
  'PK',
  'PP',
  'PR',
  'PS',
  'PT',
  'PU',
  'PX',
  'PZ',
  'RC',
  'RD',
  'RF',
  'RR',
  'RU',
  'SA',
  'SC',
  'SN',
  'SP',
  'SR',
  'SS',
  'TJ',
  'TP',
  'TS',
  'TU',
  'XC',
  'XD',
  'ZZ'
]

function AthenaAutoComplete (props) {
  const [inputValue, setInputValue] = useState('')
  const [value, setValue] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (props.disabled) {
      setOptions([])
      setValue(null)
      setInputValue('')
      return
    }

    const timeout = setTimeout(async () => {
      setLoading(true)
      try {
        const suggestions = await fetchFromAthena({
          queryName: props.queryName,
          headers: props.buildHeaders(inputValue),
          accessToken: props.credentials.user.accessToken,
          credentials: props.credentials
        })
        setOptions((suggestions || []).map(hit => ({
          id: hit.id,
          hit: hit.suggestion
        })))
      } catch (e) {
        console.log(e)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timeout)
  }, [inputValue, props.disabled, props.queryName])

  return (
    <Autocomplete
      id={props.id}
      style={{ width: props.width }}
      options={options}
      loading={loading}
      autoComplete
      autoSelect
      autoHighlight
      openOnFocus
      disabled={props.disabled}
      value={value}
      inputValue={inputValue}
      getOptionLabel={option => `${option.hit || ''}`}
      getOptionSelected={(option, selected) => option.id === selected.id}
      onChange={(event, newValue) => {
        setValue(newValue)
        props.onChange(newValue ? newValue.id : '')
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={props.label}
          margin='dense'
          variant={props.variant || 'outlined'}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <span />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color='inherit' size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={option => {
        return <div style={{ fontSize: '14px' }}>{option.hit}</div>
      }}
    />
  )
}
